import React, { useEffect, useRef, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';
import Icon from './Icon';

/**
 * Toast variant types
 */
type ToastVariant = 'success' | 'error' | 'info' | 'warning';

/**
 * Toast position types
 */
type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';

/**
 * Toast component props
 */
interface ToastProps {
  id: string;
  variant?: ToastVariant;
  title: string;
  message?: string;
  visible?: boolean;
  onClose: (id: string) => void;
  autoClose?: number;
  position?: ToastPosition;
  className?: string;
}

/**
 * Variant properties interface
 */
interface VariantProperties {
  icon: JSX.Element;
  bgColor: string;
  borderColor: string;
  textColor: string;
  progressColor: string;
}

/**
 * Toast component for displaying temporary notifications
 */
const Toast: React.FC<ToastProps> = ({
  id,
  variant = 'info',
  title,
  message,
  visible = true,
  onClose,
  autoClose = 5000,
  className = '',
}) => {
  const progressRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Trigger CSS transition in after mount
  useEffect(() => {
    if (visible) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [visible]);

  // Auto-close logic
  useEffect(() => {
    if (!visible || autoClose <= 0) return undefined;

    timerRef.current = setTimeout(() => {
      onClose(id);
    }, autoClose);

    if (progressRef.current) {
      progressRef.current.style.transition = `width ${autoClose}ms linear`;
      progressRef.current.style.width = '0%';
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [visible, autoClose, id, onClose]);

  // Reset timer on hover
  const handleMouseEnter = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (progressRef.current) {
      const computedStyle = getComputedStyle(progressRef.current);
      const width = computedStyle.width;
      progressRef.current.style.transition = 'none';
      progressRef.current.style.width = width;
    }
  };

  // Resume timer on hover out
  const handleMouseLeave = (): void => {
    if (autoClose <= 0) return;

    let remainingTime = autoClose;
    if (progressRef.current && progressRef.current.parentElement) {
      const computedStyle = getComputedStyle(progressRef.current);
      const parentWidth = progressRef.current.parentElement.offsetWidth;
      if (parentWidth) {
        const width = parseFloat(computedStyle.width) / parentWidth * 100;
        remainingTime = (autoClose * width) / 100;
        progressRef.current.style.transition = `width ${remainingTime}ms linear`;
        progressRef.current.style.width = '0%';
      }
    }

    timerRef.current = setTimeout(() => {
      onClose(id);
    }, remainingTime);
  };

  // Get variant-specific properties
  const getVariantProperties = (): VariantProperties => {
    switch (variant) {
      case 'success':
        return {
          icon: <Icon icon={CheckCircle} size="md" />,
          bgColor: 'bg-green-900/20',
          borderColor: 'border-success',
          textColor: 'text-green-400',
          progressColor: 'bg-success',
        };
      case 'error':
        return {
          icon: <Icon icon={AlertCircle} size="md" />,
          bgColor: 'bg-red-900/20',
          borderColor: 'border-error',
          textColor: 'text-red-400',
          progressColor: 'bg-error',
        };
      case 'warning':
        return {
          icon: <Icon icon={AlertTriangle} size="md" />,
          bgColor: 'bg-yellow-900/20',
          borderColor: 'border-warning',
          textColor: 'text-yellow-400',
          progressColor: 'bg-warning',
        };
      case 'info':
      default:
        return {
          icon: <Icon icon={Info} size="md" />,
          bgColor: 'bg-blue-900/20',
          borderColor: 'border-blue-500',
          textColor: 'text-blue-400',
          progressColor: 'bg-blue-500',
        };
    }
  };

  const { icon, bgColor, borderColor, textColor, progressColor } = getVariantProperties();

  return (
    <div
      role="alert"
      id={`toast-${id}`}
      className={cn(
        'relative overflow-hidden',
        'rounded-lg shadow-lg border',
        'p-4 min-w-[320px] max-w-sm',
        bgColor,
        borderColor,
        textColor,
        className
      )}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
        transition: 'opacity 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Content */}
      <div className="flex">
        <div className="flex-shrink-0 mr-3">
          {icon}
        </div>
        <div className="flex-1">
          <div className="font-medium mb-1">{title}</div>
          {message && <div className="text-sm opacity-90">{message}</div>}
        </div>
        <button
          className="ml-3 flex-shrink-0 focus:outline-none focus:ring-1 focus:ring-accent/50 rounded"
          onClick={() => onClose(id)}
          aria-label="Close notification"
        >
          <Icon icon={X} size="sm" />
        </button>
      </div>

      {/* Progress bar for auto-close */}
      {autoClose > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <div
            ref={progressRef}
            className={cn('h-full w-full', progressColor)}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
